import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toRequestFailure, useGoogleLocation } from '@/features/google-location'
import {
  RestaurantSettingsFormOutput,
  restaurantSettingsFormSchema,
  toFormValues,
  useRestaurantSettingsStore,
  validationMessageKey
} from '@/features/restaurant-settings'
import DynamicForm from '@/shared/components/DynamicForm/DynamicForm'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import RestaurantSwitcher from '../components/RestaurantSwitcher'
import SettingsPanel from '../components/SettingsPanel'
import { useRestaurantFormConfig } from '../forms/useRestaurantFormConfig'
import { useSettingsModal } from '../store/useSettingsModal'
import styles from '../SettingsModal.module.scss'
import { GooglePlaceDraft } from './GoogleLocationBlocks'

const FORM_ID = 'restaurant-settings-form'

interface RestaurantSectionProps {
  /** Reports edits not yet saved, which leaving would throw away. */
  onUnsavedChange: (hasUnsaved: boolean) => void
}

/**
 * Everything about the open venue as one DynamicForm
 * (`useRestaurantFormConfig`), written by one Save and only when all of it
 * passes: the required fields are checked together, so an address missing its
 * city is not kept while the rest of the form is. The Google place is part of
 * it: a pick waits for this Save with the address it filled in. Only the logo
 * is saved as it is picked, a request to the backend of its own.
 */
function RestaurantSection({ onUnsavedChange }: RestaurantSectionProps) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const settings = useRestaurantSettingsStore(s => s.settings)
  const saveIdentity = useRestaurantSettingsStore(s => s.saveIdentity)
  const saveAddress = useRestaurantSettingsStore(s => s.saveAddress)
  const saveContact = useRestaurantSettingsStore(s => s.saveContact)
  const saveHours = useRestaurantSettingsStore(s => s.saveHours)

  /*
    The Google place's draft lives here, beside the form rather than in it:
    the form's values are the settings document and its schema strips what
    is not part of it. It counts as unsaved like any edit, leaving drops it
    and Save keeps it.
  */
  const [placeDraft, setPlaceDraft] = useState<GooglePlaceDraft | null>(null)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const { save: savePlace, disconnect: disconnectPlace } = useGoogleLocation()

  const hasUnsaved = isFormDirty || placeDraft !== null
  useEffect(() => {
    onUnsavedChange(hasUnsaved)
  }, [hasUnsaved, onUnsavedChange])

  const fields = useRestaurantFormConfig(
    settings.contentLanguage,
    settings.address.timezone,
    placeDraft,
    setPlaceDraft
  )

  // Re-made when the saved document changes, which resets the form to it: on
  // hydration, and after a Save, which hands the values back trimmed.
  const defaultValues = useMemo(
    () =>
      toFormValues(settings, form =>
        t(`RESTAURANT_SETTINGS.VENUE.LEGAL_FORM.${form.toUpperCase()}`)
      ),
    [settings, t]
  )

  /*
    Save is never greyed out (SettingsPanel), so a second press while the
    place is on its way is ignored here.
  */
  const isSaving = useRef(false)

  // Bumped to start the form again from what is saved.
  const [formKey, setFormKey] = useState(0)

  /*
    First, as the one part that can fail on the way: if it does, nothing else
    is written, so the form stays as the owner left it to try again. A pick
    left for over a day is refused (409) and has to be made again.
  */
  const keepPlace = async (draft: GooglePlaceDraft) => {
    try {
      if (draft.kind === 'pick')
        await savePlace.mutateAsync({ placeId: draft.location.placeId })
      else await disconnectPlace.mutateAsync()
      setPlaceDraft(null)
      return true
    } catch (error) {
      const isExpired = toRequestFailure(error).kind === 'not-picked'
      showSnackbar(
        t('ALERTS.GOOGLE_LOCATION_SAVE_ERROR.TITLE'),
        t(
          isExpired
            ? 'ALERTS.GOOGLE_LOCATION_SAVE_ERROR.EXPIRED'
            : 'ALERTS.GOOGLE_LOCATION_SAVE_ERROR.DESCRIPTION'
        ),
        'error'
      )
      return false
    }
  }

  const save = async (values: Record<string, unknown>) => {
    if (isSaving.current) return
    isSaving.current = true
    try {
      if (placeDraft && !(await keepPlace(placeDraft))) return
      saveSettings(values)
    } finally {
      isSaving.current = false
    }
  }

  const saveSettings = (values: Record<string, unknown>) => {
    const next = values as RestaurantSettingsFormOutput
    const changed = <T,>(part: T, saved: T) =>
      JSON.stringify(part) !== JSON.stringify(saved)

    const identityChanged = changed(next.identity, settings.identity)
    const addressChanged = changed(next.address, settings.address)
    const contactChanged = changed(next.contact, settings.contact)
    const hoursChanged = changed(next.hours, settings.hours)

    if (identityChanged) saveIdentity(next.identity)
    if (addressChanged) saveAddress(next.address)
    if (contactChanged) saveContact(next.contact)
    if (hoursChanged) saveHours(next.hours)
    // An edit that the save trims away (a trailing space, a postcode's dash)
    // stores nothing, so the store's reset never comes; the form is put back
    // to what is saved by hand, or it stays "unsaved" and leaving asks.
    if (!identityChanged && !addressChanged && !contactChanged && !hoursChanged)
      setFormKey(key => key + 1)
    showSnackbar(
      t('ALERTS.SETTINGS_SAVE_SUCCESS.TITLE'),
      t('ALERTS.SETTINGS_SAVE_SUCCESS.DESCRIPTION'),
      'success'
    )
  }

  const refuse = () => {
    showSnackbar(
      t('ALERTS.SETTINGS_INVALID.TITLE'),
      t('ALERTS.SETTINGS_INVALID.DESCRIPTION'),
      'error'
    )
    // The field at fault may be several sections above the Save just
    // pressed; brought into view once its error has rendered.
    requestAnimationFrame(() =>
      document
        .getElementById(FORM_ID)
        ?.querySelector('[aria-invalid="true"]')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    )
  }

  // Opened for one block, such as the Google place from the website builder:
  // brought to the top of the panel rather than left below the fold.
  const anchor = useSettingsModal(s => s.anchor)
  useEffect(() => {
    if (anchor)
      document.getElementById(anchor)?.scrollIntoView({ block: 'start' })
  }, [anchor])

  return (
    <SettingsPanel
      lead={t('RESTAURANT_SETTINGS.LEAD')}
      footer={{ formId: FORM_ID }}
    >
      <div className={styles.blocks}>
        <RestaurantSwitcher hasUnsaved={hasUnsaved} />
        <DynamicForm
          key={formKey}
          id={FORM_ID}
          fields={fields}
          validationSchema={restaurantSettingsFormSchema}
          defaultValues={defaultValues}
          onSubmit={save}
          onInvalid={refuse}
          onIsDirtyChange={setIsFormDirty}
          // The schemas raise codes, not sentences: they have no `t()`.
          errorMessage={code => t(validationMessageKey(code))}
          // Checked on Save rather than as it is typed: a NIP half typed is
          // not yet wrong.
          validationMode="onSubmit"
          superCondensed
          sectioned
        />
      </div>
    </SettingsPanel>
  )
}

export default RestaurantSection
